import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useConfirm } from './ConfirmProvider';
import './ui.css';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Open pop-ups, newest last: only the top one answers Escape and Tab
const stack = [];

// The text for "you've typed something and are about to lose it"
export const DISCARD = {
  title: 'Discard your changes?',
  message: "You've changed this but haven't saved. If you leave now, the changes are lost.",
  confirmLabel: 'Discard',
  cancelLabel: 'Keep editing',
};

/**
 * The one pop-up. Slides up from the bottom on phones, centred on wider
 * screens. Escape, the × and tapping outside all close it; if `dirty`, they
 * ask first. Focus moves in and comes back to where it was; the page behind
 * doesn't scroll.
 *
 * title: the heading
 * onClose(): called once it should close
 * dirty: something typed that closing would lose
 * onSubmit(e): makes the pop-up a form, so Enter saves
 * footer: the buttons, or (requestClose) => buttons, so Cancel can ask first
 *   too. Wrap them in <div className="bk-actions">.
 * wide: a wider pop-up on bigger screens
 */
export default function Sheet({ title, onClose, dirty = false, onSubmit, footer, wide = false, initialFocus, children, labelledBy }) {
  const headingId = useId();
  const ref = useRef(null);
  const confirm = useConfirm();
  const state = useRef({});
  state.current = { dirty, onClose };

  const requestClose = async () => {
    if (state.current.dirty && !(await confirm(DISCARD))) return;
    state.current.onClose();
  };
  const requestRef = useRef(requestClose);
  requestRef.current = requestClose;

  useEffect(() => {
    const token = {};
    stack.push(token);
    const previous = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const box = ref.current;
    const first = (initialFocus && box.querySelector(initialFocus))
      || box.querySelector('input:not([type=hidden]):not([disabled]), select, textarea')
      || box.querySelector(FOCUSABLE);
    if (first) first.focus();
    const onKeyDown = (e) => {
      if (stack[stack.length - 1] !== token) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        requestRef.current();
        return;
      }
      if (e.key === 'Tab') {
        const items = Array.from(box.querySelectorAll(FOCUSABLE));
        if (!items.length) { e.preventDefault(); return; }
        const firstItem = items[0];
        const last = items[items.length - 1];
        if (!box.contains(document.activeElement)) { e.preventDefault(); (e.shiftKey ? last : firstItem).focus(); }
        else if (e.shiftKey && document.activeElement === firstItem) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); firstItem.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      stack.splice(stack.indexOf(token), 1);
      if (!stack.length) document.body.style.overflow = overflow;
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) previous.focus();
    };
    // Runs once per opening
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Body = onSubmit ? 'form' : 'div';
  const bodyProps = onSubmit ? { onSubmit: (e) => { e.preventDefault(); onSubmit(e); }, noValidate: true } : {};

  return createPortal(
    <div className="ui-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
      <Body
        ref={ref}
        className={`ui-sheet${wide ? ' ui-sheet--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy || headingId}
        {...bodyProps}
      >
        <span className="ui-sheet__grab" aria-hidden="true" />
        <div className="ui-sheet__head">
          <h2 className="ui-sheet__title" id={headingId}>{title}</h2>
          <button type="button" className="ui-icon-btn" aria-label="Close" onClick={requestClose}>
            <XMarkIcon aria-hidden="true" />
          </button>
        </div>
        {children}
        {typeof footer === 'function' ? footer(requestClose) : footer}
      </Body>
    </div>,
    document.body,
  );
}
